import type { components } from './api-v2'

/**
 * Typed client for the PUBLIC status-page reads served by the Rust v2 backend
 * (uptrack-server, `GET /api/status/*`). Response types come straight from the
 * generated OpenAPI schemas (`api-v2.ts`) so the FE is checked against the Rust
 * contract — the read cutover happens WITH the FE (v2-native), not by retrofitting
 * the old Phoenix shape (see memory project_frontend_tanstack_rust_be).
 *
 * Plain `fetch` (no extra dep). Same `VITE_API_URL` base as the legacy client; in
 * prod that host routes `/api/status/*` to uptrack-server once haproxy's
 * rustV2ApiPaths includes it.
 */
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000'

type Schemas = components['schemas']

/** `GET /api/status/{slug}` payload (`{ data: ... }` envelope). */
export type StatusPageData = Schemas['ShowPublicData']
export type StatusMonitor = Schemas['ShowMonitor']
export type StatusIncident = Schemas['ShowIncident']
export type StatusMaintenance = Schemas['ShowMaintenance']
export type StatusUptime = Schemas['UptimeResponse']
export type StatusHistory = Schemas['HistoryResponse']
export type StatusMonitorRegions = Schemas['MonitorRegions']

/** `GET /api/status/{slug}/regions` payload. */
export type StatusRegions = Schemas['RegionsResponse']

/** `theme_config` jsonb is `unknown` in the schema; this is its known shape. */
export interface StatusPageTheme {
  background_color?: string
  text_color?: string
  font_family?: string
}

export class StatusApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'StatusApiError'
  }
}

/** Build a StatusApiError, surfacing the server's `error`/`message` when present. */
async function statusError(res: Response, path: string): Promise<StatusApiError> {
  let message = `status API ${path} failed: ${res.status}`
  try {
    const body = (await res.json()) as { error?: string; message?: string }
    if (body?.error) message = body.error
    else if (body?.message) message = body.message
  } catch {
    /* non-JSON body — keep the default message */
  }
  return new StatusApiError(res.status, message)
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw await statusError(res, path)
  return (await res.json()) as T
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw await statusError(res, path)
  return (await res.json()) as T
}

const enc = encodeURIComponent

export const statusV2 = {
  /** Public status page (header, overall status, monitors, incidents, maintenance). */
  getStatusPage: (slug: string) =>
    getJson<{ data: StatusPageData }>(`/api/status/${enc(slug)}`),

  /** Per-monitor uptime % over the default window. */
  getUptime: (slug: string) => getJson<StatusUptime>(`/api/status/${enc(slug)}/uptime`),

  /** Per-monitor daily uptime series (the sparkline source), default 7 days. */
  getHistory: (slug: string, days?: number) =>
    getJson<StatusHistory>(`/api/status/${enc(slug)}/history${days ? `?days=${days}` : ''}`),

  /** Per-monitor, per-region latest status + response time. */
  getRegions: (slug: string) => getJson<StatusRegions>(`/api/status/${enc(slug)}/regions`),

  /** Subscribe an email to a public status page (POST). Not in the OpenAPI spec
   *  yet, so the response is typed by hand to match the Rust handler. */
  subscribe: (slug: string, email: string) =>
    postJson<{ success: boolean; message: string }>(`/api/status/${enc(slug)}/subscribe`, { email }),
}
